/**
 * Normalizes a location string for use as a URL path segment.
 * Produces slugs like "helsinki_finland", "etatyo_eurooppa" (lowercase is canonical). A
 * "Remote - X" style dash separator collapses to a single underscore like any other word
 * separator — see {@link decodeLocationSlug} for how the dash is restored on decode.
 * - No commas, spaces, or special characters (colons, parentheses, apostrophes, etc.)
 * - Diacritics (ä, ö, å, …) are ASCII-folded (NFD-normalize + strip combining marks), e.g.
 *   "Jyvaskyla" from "Jyväskylä", "etatyo" from "etätyö" - the same technique already used for
 *   company slugs (sanitizeForUrl in utils/routing/generate-url.ts), so both slug families
 *   behave the same way and one canonical URL exists per location (TEC-472; this function used
 *   to deliberately preserve diacritics, which the spec calls out as the wrong behavior).
 *   Callers that need to reconstruct the original spelling for display or search-facet
 *   filtering must not just title-case the slug back - see decodeLocationSlug, which restores
 *   known Finnish place names via the GeoNames gazetteer (finland-geonames-lookup.ts).
 *
 * Used by: Algolia router, url-handling, +Head canonical/breadcrumb, redirects.
 */
export function toLocationSlug(raw: string | null | undefined): string {
  if (raw == null || typeof raw !== 'string') return '';
  let s = raw.trim();
  if (!s) return '';
  // ASCII-fold diacritics before anything else: NFD-decompose ("a" + combining diaeresis for
  // "ä"), then strip the combining marks. Same technique as sanitizeForUrl in generate-url.ts.
  s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // Comma (with optional spaces) and remaining whitespace → underscore.
  s = s.replace(/,\s*/g, '_').replace(/\s+/g, '_');
  // Strip any remaining character that is not a letter (including any extended-Latin letter the
  // NFD fold above didn't decompose), digit, underscore, or hyphen. This removes colons,
  // parentheses, apostrophes, etc.
  s = s.replace(/[^a-zA-Z0-9\u00C0-\u024F_-]/g, '');
  // Collapse a "word - word" separator (however it arrived) down to a single underscore.
  s = s.replace(/_-_/g, '_');
  // Collapse multiple underscores
  s = s.replace(/__+/g, '_');
  // Strip leading/trailing underscores (e.g. ", Finland" → "_Finland" → "Finland")
  s = s.replace(/^_+|_+$/g, '');
  // Lowercase is the canonical URL casing — avoids duplicate-URL indexing for casing
  // variants (e.g. Helsinki_Finland vs helsinki_finland).
  return s.toLowerCase();
}
